/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable import/no-named-default */
import { openImageViewer } from '@actions/common';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {
  default as Close,
  default as CloseIcon,
} from '@material-ui/icons/Close';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  cancelChatMessage,
  MimeTypeUnsupported,
  selectAttachmentQueue,
} from '@reducers/chatMessageQueue';
import {
  MediaType,
  MessagingProviderSmallIcon,
} from '@src/model/frontendmodel';
import { activeChatIdSelector, activeChatSelector } from '@src/selectors/chats';
import { ChatTray } from '@src/types/chats';
import { useSnackbar } from 'notistack';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import './AttachFilesSumaryAll.css';

const AttachFilesSummaryAll = injectIntl(({ intl: { formatMessage } }) => {
  const chatId = useSelector(activeChatIdSelector) ?? '';
  const chat = useSelector(activeChatSelector) as ChatTray;
  const attachedFiles = useSelector(selectAttachmentQueue(chatId));
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  if (!chatId || !chat?.provider || attachedFiles.length === 0) return null;

  attachedFiles
    .filter(({ error }) => error)
    // eslint-disable-next-line array-callback-return
    .map(({ id, error, message: { file } }) => {
      enqueueSnackbar(null, {
        key: id,
        autoHideDuration: 5000,
        preventDuplicate: true,
        disableWindowBlurListener: true,
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        content(key) {
          return (
            <Alert
              key={key}
              severity="error"
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => {
                    dispatch(cancelChatMessage({ id, reason: 'cancelled' }));
                    closeSnackbar(id);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <AlertTitle>{file.name}</AlertTitle>
              {formatMessage(
                { id: `AttachFileError.${error!.message}` },
                {
                  type: file.type,
                  ...('maxSizeMB' in error! && { maxSizeMB: error?.maxSizeMB }),
                }
              )}
              {error! instanceof MimeTypeUnsupported && (
                <img
                  style={{
                    width: '1.5em',
                    marginLeft: '0.2em',
                    marginBottom: '-0.5em',
                  }}
                  src={MessagingProviderSmallIcon[chat?.provider]}
                  alt={chat?.provider}
                />
              )}
            </Alert>
          );
        },
      });
    });

  const successAttachedFiles = attachedFiles.filter(
    ({ loading }) =>
      loading.includes('uploading-file') ||
      loading.includes('upload-file-success')
  );

  return (
    <>
      {successAttachedFiles.length > 0 && (
        <div className="attach-file-summary__container">
          <Grid
            className="attach-file-summary__grid"
            container
            spacing={1}
            wrap="nowrap"
          >
            {successAttachedFiles.map(
              ({ id, message: { type, file, url }, loading }) => (
                <Grid key={id} item>
                  <Tooltip
                    title={<Typography variant="body2">{file.name}</Typography>}
                    placement="top"
                    arrow
                    data-type={type}
                  >
                    <Paper
                      className="attach-file-summary__item"
                      variant="outlined"
                    >
                      <Button
                        className="attach-file-summary__close"
                        variant="contained"
                        data-uploading={loading.includes('uploading-file')}
                        onClick={() =>
                          dispatch(
                            cancelChatMessage({ id, reason: 'cancelled' })
                          )
                        }
                      >
                        {loading.includes('uploading-file') ? (
                          <CircularProgress
                            size="1em"
                            thickness={6}
                            disableShrink
                            color="inherit"
                          />
                        ) : (
                          <Close fontSize="inherit" />
                        )}
                      </Button>
                      {type === MediaType.IMAGE ? (
                        <Box
                          display="inline-block"
                          onClick={() => dispatch(openImageViewer(url))}
                        >
                          <img src={url} alt={file.name} />
                        </Box>
                      ) : (
                        <Grid container direction="row" wrap="nowrap">
                          <Grid item style={{ fontSize: '3rem' }}>
                            <DescriptionOutlinedIcon
                              fontSize="inherit"
                              color="action"
                            />
                          </Grid>
                          <Grid item>
                            <Typography
                              variant="body1"
                              color="textSecondary"
                              className="attach-file-summary__name"
                            >
                              <b>{file.name}</b>
                            </Typography>
                            <Typography
                              variant="body2"
                              className="attach-file-summary__mime"
                            >
                              {file.type}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  </Tooltip>
                </Grid>
              )
            )}
          </Grid>
        </div>
      )}
    </>
  );
});

export default AttachFilesSummaryAll;
